import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { STATUS } from 'utils/constants'
import { POST } from 'rest/request'
import { RootState } from 'store'
import { api } from 'rest/urls'

const initialState: playground.PlaygroundState = {
  placeId: '',
  floorId: '',
  mode: 'base',
  isLoading: false,
  refreshPosition: 0,
  isGroupSelected: false,
  status: STATUS.idle,
  recentlySelected: [],
  availableObjects: [],
  deleteObjects: {},
  warehouses: {},
  features: {},
  history: {},
  others: {},
  frames: {},
}

export const getMapData = createAsyncThunk<playground.GetPlaceResponse, string>('playground/get', async (id, thunkApi) => {
  const store = thunkApi.getState() as RootState
  const filters = store.place.filters.data
  return await POST(`${api}places/${id}/filteredmaps`, { body: { filters } })
})

export const filterMap = createAsyncThunk<playground.Map, playground.FilterMap>('playgroundMap/get', async ({ filters, mapId }) => {
  return await POST(`${api}maps/${mapId}`, { body: { filters } })
})

export const setMapData = createAsyncThunk<playground.GetPlaceResponse, object>('playground/set', async (body, thunkAPI) => {
  const store = thunkAPI.getState() as RootState
  const placeId = store.playground.placeId || ''
  return await POST(`${api}places/${placeId}/maps`, { body })
})

const processData = (state: playground.PlaygroundState, action: PayloadAction<playground.GetPlaceResponse, string>, placeId = ''): playground.PlaygroundState => {
  const { availableObjects, maps } = action.payload
  const data = maps.reduce((r, { id, objects, frame, ...features }) => {
    const { width, height } = features
    const placeFrame = frame?.length > 0 ? frame : [
      [0, 0],
      [width, 0],
      [width, height],
      [0, height]
    ]

    return {
      frames: { ...r.frames, [id]: placeFrame },
      features: { ...r.features, [id]: features },
      others: { ...r.others, [id]: objects.others },
      warehouses: { ...r.warehouses, [id]: objects.warehouses },
    }
  }, { warehouses: {}, features: {}, frames: {}, others: {} })

  return {
    ...state,
    ...data,
    history: {},
    copy: undefined,
    availableObjects,
    deleteObjects: {},
    status: STATUS.succeed,
    mode: placeId ? state.mode : 'base',
    floorId: placeId ? (maps[0]?.id || '') : state.floorId,
    placeId: placeId || state.placeId
  }
}

const playgroundSlice = createSlice({
  name: 'playground',
  initialState,
  reducers: {
    updateFloor: (state, action: PayloadAction<string>) => { state.floorId = action.payload },
    updateSelectedMag: (state, action: PayloadAction<playground.SelectedMag | undefined>) => {
      const { recentlySelected } = state
      const { payload } = action

      if (payload) {
        const listIdx = recentlySelected.findIndex(({ typeId }) => typeId === payload.typeId)
        if (listIdx > -1) {
          if (listIdx !== recentlySelected.length - 1) {
            const item = recentlySelected[listIdx]
            const newList = recentlySelected.filter((_, i) => i !== listIdx)
            newList.push(item)
            state.recentlySelected = newList
          }
        } else {
          const { placeId, typeId, name, warehouseData } = payload
          recentlySelected.push({ placeId, typeId, name, area: warehouseData!.size!.area })
          if (recentlySelected.length > 6) recentlySelected.shift()
          state.recentlySelected = recentlySelected
        }
      }
      state.selectedMag = payload
    },
    selectMags: (state, action: PayloadAction<{ ids: string[] }>) => {
      const newWarehouses: playground.ObjectWithLists<playground.Warehouse> = { ...state.warehouses }
      const { ids } = action.payload

      for (const key in newWarehouses) {
        newWarehouses[key] = newWarehouses[key].map(m => {
          if (ids.includes(m.typeId)) return { ...m, isSelected: true }
          else if (m.children) return { ...m, children: m.children.map(mm => ids.includes(mm.typeId) ? { ...mm, isSelected: true } : mm) }
          else return m
        })
      }
      state.mode = 'select'
      state.warehouses = newWarehouses
    },
    removeItemFromRecentlySelected: (state, action: PayloadAction<boolean | Pick<playground.RecentlySelectedData, "typeId" | "placeId">>) => {
      const { recentlySelected } = state
      if (typeof action.payload === 'boolean') {
        if (action.payload) {
          const lastItem = recentlySelected.pop()
          state.recentlySelected = lastItem ? [lastItem] : []
        }

      } else {
        const { typeId, placeId } = action.payload
        state.recentlySelected = recentlySelected.filter(e => e.typeId !== typeId || e.placeId !== placeId)
      }
    },
    updateAvailableObjects: (state, action) => { state.availableObjects = action.payload },
    switchMode: (state, action: PayloadAction<playground.Mode>) => {
      const { mode, selectedMag, floorId, warehouses } = state

      if (mode === 'base' && selectedMag) {
        const selectedId = selectedMag.typeId
        state.warehouses[floorId] = warehouses[floorId].map(warehouse => {
          if (warehouse.children) return {
            ...warehouse,
            children: warehouse.children.map(w => w.typeId === selectedId ? { ...w, isSelected: true } : w)
          } // satisfies playground.Warehouse
          return (warehouse.typeId === selectedId ? { ...warehouse, isSelected: true } : warehouse) // satisfies playground.Warehouse
        })

      } else if (mode === 'select') {
        state.warehouses[floorId] = warehouses[floorId].map(warehouse => {
          if (warehouse.children) return {
            ...warehouse,
            children: warehouse.children.map(w => w.isSelected ? { ...w, isSelected: false } : w)
          } // satisfies playground.Warehouse
          return (warehouse.isSelected ? { ...warehouse, isSelected: false } : warehouse) // satisfies playground.Warehouse
        })
      }
      state.mode = action.payload
    },
    switchEditMode: (state, _: PayloadAction<undefined>) => {
      const { mode, warehouses, others, availableObjects, frames, copy } = state

      if (mode === 'edit' && copy) {
        state.availableObjects = copy.availableObjects
        state.warehouses = copy.warehouses
        state.others = copy.others
        state.frames = copy.frames
        state.copy = undefined

      } else {
        state.copy = { availableObjects, warehouses, others, frames }
      }
      state.mode = mode === 'edit' ? 'base' : 'edit'
    },
    selectWarehouseGroup: (state, action: PayloadAction<{ id?: string, type: 'select' | 'deselect' }>) => {
      const { warehouses, floorId } = state
      const { id, type } = action.payload

      if (type === 'select' && id) {
        const warehouse = warehouses[floorId].find(e => e.id === id)
        if (warehouse && warehouse.children) {
          const { width, height, rotation, children } = warehouse
          state.features[id] = { width, height, isGrouped: true, rotation, name: '', background: { url: '' } }
          state.warehouses[id] = children
          state.floorId = id
          state.isGroupSelected = true
        }

      } else if (type === 'deselect') {
        let parnet = { id: '', idx: -1 }
        for (const warehouse in warehouses) {
          const idx = warehouses[warehouse]?.findIndex(e => e.id === floorId)
          if (idx > -1) {
            parnet.id = warehouse
            parnet.idx = idx
            break
          }
        }

        const children = warehouses[floorId]
        if (parnet.id && warehouses[parnet.id] && children) {
          state.warehouses[parnet.id][parnet.idx].children = children.map(e => ({ ...e, isSelected: false }))
          delete state.warehouses[floorId]
          delete state.features[floorId]
          state.isGroupSelected = false
          state.floorId = parnet.id
        }
      }
    },
    updatePlace: (state, action) => {
      const { frames = {}, floorId } = state
      const place = frames[floorId]
      const { payload } = action

      if (place && payload) {
        state.frames[floorId] = (({ idx = 0, remove = 0, element }) => {
          element ? place.splice(idx, remove, element) : place.splice(idx, remove)
          return place
        })(payload)
      }
    },
    addObjects: (state, action: PayloadAction<{ type?: playground.Object_Type, list: playground.Base_Object[] }>) => {
      const { list, type = 'warehouses' } = action.payload
      const { floorId } = state

      if (state[type] && state[type][floorId] && list) {
        state[type][floorId] = [...state[type][floorId], ...list]
      }
    },
    updateObjects: (state, action: PayloadAction<{ type?: playground.Object_Type, values: any }>) => {
      const { type = 'warehouses', values } = action.payload
      const { floorId } = state

      if (state[type][floorId] && values) {
        state[type][floorId] = state[type][floorId].map(e => {
          if (e.children) {
            return (e.id && values[e.id])
              ? { ...e, ...values[e.id] }
              : { ...e, children: e.children.map(c => (c.id && values[c.id]) ? { ...c, ...values[c.id] } : c) }
          }
          return (e.id && values[e.id]) ? { ...e, ...values[e.id] } : e
        })
      }
    },
    updateObject: (state, action: PayloadAction<{ type?: playground.Object_Type, id: string, values: any, status?: playground.Warehouse_Status }>) => {
      const { type = 'warehouses', id, values, status } = action.payload
      const { selectedMag } = state

      if (selectedMag && selectedMag.typeId === id) {
        state.selectedMag = { ...selectedMag, ...values }
      }
      Object.entries(state[type] as playground.ObjectWithLists<playground.Base_Object>)
        .forEach(([floorId, v]) => {
          const index = v.findIndex(({ typeId }) => typeId === id)
          if (index > -1) {
            const newValue: playground.Warehouse = (status && 'warehouseData' in v[index])
              ? {
                ...v[index],
                ...values,
                warehouseData: {
                  ...(v[index] as playground.Warehouse).warehouseData,
                  status
                }
              }
              : {
                ...v[index],
                ...values
              }
            state[type][floorId][index] = newValue
          }
        })
    },
    removeObject: (state, action: PayloadAction<{ type?: playground.Object_Type, id: string }>) => {
      const { id, type = 'warehouses' } = action.payload
      const { floorId, deleteObjects } = state

      if (state[type][floorId] && id) {
        const { list, deletedObject } = (<T extends playground.Base_Object>(list: T[]) => list.reduce((r, e) => {
          if (e.id !== id) r.list.push(e)
          else if (!e.isNew) { r.deletedObject = e }
          return r
        }, {
          list: [] as T[],
          deletedObject: undefined as undefined | T
        }))(state[type][floorId])

        if (deletedObject) {
          const deletedObjectsOnThatFloor = deleteObjects[floorId] || []
          state.deleteObjects = { ...deleteObjects, [floorId]: [...deletedObjectsOnThatFloor, deletedObject] }
        }
        state[type][floorId] = list
      }
    },
    refreshPosition: (state) => {
      state.refreshPosition = Date.now()
    },
    clearMap: () => {
      return initialState
    }
  },
  extraReducers: (builder) => {
    builder.addCase(setMapData.fulfilled, processData)
    builder.addCase(getMapData.pending, (state, _) => { state.status = STATUS.pending })
    builder.addCase(getMapData.rejected, (state, _) => { state.status = STATUS.failed })
    builder.addCase(getMapData.fulfilled, (state, action) => processData(state, action, action.meta.arg))
    builder.addCase(filterMap.pending, (state, _) => { state.isLoading = true })
    builder.addCase(filterMap.rejected, (state, _) => { state.isLoading = false })
    builder.addCase(filterMap.fulfilled, (state, action) => {
      const { objects: { warehouses, others }, id } = action.payload
      state.isLoading = false
      if (warehouses) state.warehouses[id] = warehouses
      if (others) state.others[id] = others
    })
  }
})

export const { updateFloor, updateSelectedMag, updateAvailableObjects, switchEditMode, selectWarehouseGroup, updatePlace, addObjects, updateObject, updateObjects, switchMode, selectMags, removeObject, removeItemFromRecentlySelected, refreshPosition, clearMap } = playgroundSlice.actions

const selectPlaygroundObject: playground.SelectPlaygroundObject<RootState> = (state, type, defaultVal = []) => {
  const { floorId } = state.playground
  return state.playground[type][floorId] || defaultVal
}

export const selectPlayground = (state: RootState) => state.playground
export const selectPlaygroundMode = (state: RootState) => state.playground.mode
export const selectPlaygroundFloor = (state: RootState) => state.playground.floorId
export const selectPlaygroundStatus = (state: RootState) => state.playground.status
export const selectPlaygroundIsLoading = (state: RootState) => state.playground.isLoading
export const selectPlaygroundPlaceId = (state: RootState) => state.playground.placeId
export const selectPlaygroundAllFeatures = (state: RootState) => state.playground.features
export const selectPlaygroundSelectedMag = (state: RootState) => state.playground.selectedMag
export const selectPlaygroundEditMode = (state: RootState) => state.playground.mode === 'edit'
export const selectPlaygroundGroupSelected = (state: RootState) => state.playground.isGroupSelected
export const selectPlaygroundRefreshPosition = (state: RootState) => state.playground.refreshPosition
export const selectPlaygroundAvailableObjects = (state: RootState) => state.playground.availableObjects
export const selectPlaygroundRecentlySelected = (state: RootState) => state.playground.recentlySelected

export const selectPlaygroundFrame = (state: RootState) => selectPlaygroundObject(state, 'frames')
export const selectPlaygroundOthers = (state: RootState) => selectPlaygroundObject(state, 'others')
export const selectPlaygroundFeatures = (state: RootState) => selectPlaygroundObject(state, 'features', {})
export const selectPlaygroundWarehouses = (state: RootState) => selectPlaygroundObject(state, 'warehouses')

export default playgroundSlice.reducer